import { z } from '@hono/zod-openapi';

// ponytail: labels are plain strings, add a label table if renames/colours are needed
export const LabelSchema = z
  .string()
  .regex(/^[a-z0-9-]{1,32}$/, 'Label must be 1-32 lowercase letters, digits or dashes')
  .openapi({ example: 'test', description: 'Configuration label, e.g. live, test, dev' });

export const LabelPermissionSchema = z
  .object({
    label: LabelSchema,
    access: z.enum(['view', 'edit']).openapi({
      example: 'view',
      description: 'view: see configurations and sessions with this label. edit: also change those configurations.',
    }),
  })
  .openapi('LabelPermission');

export const UserRoleSchema = z.enum(['admin', 'member']).openapi({
  example: 'member',
  description: 'admin: sees everything and manages users. member: access limited to `permissions`.',
});

export const UserSchema = z
  .object({
    id: z.string(),
    email: z.string(),
    role: UserRoleSchema,
    permissions: z.array(LabelPermissionSchema),
    isActive: z.boolean(),
    createdAt: z.date().or(z.string()),
    lastLoginAt: z.date().or(z.string()).nullable(),
  })
  .openapi('User');

export const UserCreateSchema = z
  .object({
    email: z.string().email(),
    password: z.string().min(12).openapi({ description: 'Initial password, at least 12 characters' }),
    role: UserRoleSchema,
    permissions: z.array(LabelPermissionSchema).default([]),
    isActive: z.boolean().default(true),
  })
  .openapi('UserCreate');

export const UserUpdateSchema = UserCreateSchema.extend({
  password: z.string().min(12).optional().openapi({ description: 'New password; omit to keep the current one' }),
}).openapi('UserUpdate');

export const UserIdParamSchema = z.object({
  id: z.string().openapi({ param: { name: 'id', in: 'path' }, example: 'usr_abc123', description: 'User ID' }),
});

export const MeSchema = UserSchema.pick({ id: true, email: true, role: true, permissions: true }).openapi('Me');

const TokenLabelsSchema = z
  .array(LabelSchema)
  .nullable()
  .openapi({ description: 'Configuration labels this token may use (view access). null = all labels.' });

export const IntegrationTokenSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    labels: TokenLabelsSchema,
    isActive: z.boolean(),
    expiresAt: z.date().or(z.string()).nullable(),
    lastUsedAt: z.date().or(z.string()).nullable(),
    createdAt: z.date().or(z.string()),
  })
  .openapi('IntegrationToken');

export const IntegrationTokenCreateSchema = z
  .object({
    name: z.string().trim().min(1).openapi({ example: 'Partner API' }),
    labels: TokenLabelsSchema,
    expiresAt: z.coerce.date().nullable().default(null).openapi({ description: 'Optional expiry; null = never' }),
  })
  .openapi('IntegrationTokenCreate');

export const IntegrationTokenUpdateSchema = IntegrationTokenCreateSchema.extend({
  isActive: z.boolean().openapi({ description: 'false revokes the token on its next request' }),
}).openapi('IntegrationTokenUpdate');

export const IntegrationTokenCreatedSchema = IntegrationTokenSchema.extend({
  token: z.string().openapi({ description: 'The bearer token. Returned only once; only its hash is stored.' }),
}).openapi('IntegrationTokenCreated');

export type LabelPermission = z.infer<typeof LabelPermissionSchema>;
export type User = z.infer<typeof UserSchema>;
export type UserCreate = z.infer<typeof UserCreateSchema>;
export type UserUpdate = z.infer<typeof UserUpdateSchema>;
export type Me = z.infer<typeof MeSchema>;
export type IntegrationToken = z.infer<typeof IntegrationTokenSchema>;
export type IntegrationTokenCreate = z.input<typeof IntegrationTokenCreateSchema>;
export type IntegrationTokenUpdate = z.input<typeof IntegrationTokenUpdateSchema>;
export type IntegrationTokenCreated = z.infer<typeof IntegrationTokenCreatedSchema>;

/** 'all' for admins and unrestricted integration tokens; otherwise the label grants. */
export type Access = 'all' | LabelPermission[];

/** Visible when any of its labels is granted. Unlabeled records are admin-only. */
export function canView(access: Access, labels: readonly string[]): boolean {
  return access === 'all' || labels.some((label) => access.some((p) => p.label === label));
}

/** Editable only with edit on every label, so a test editor can't touch a [test, live] config. */
export function canEdit(access: Access, labels: readonly string[]): boolean {
  return (
    access === 'all' ||
    (labels.length > 0 && labels.every((label) => access.some((p) => p.label === label && p.access === 'edit')))
  );
}
